import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageSeo from './components/PageSeo';
import { useDarkMode } from './DarkModeContext';
import { AnimatedBeam } from './AnimatedBeam';
import './App.css';
import { trackEvent } from './utils/amplitude';
import Footer from './Footer';

const scriptOptions = [
    "I don't know who needs to hear this but...",
    "Guys, this is wild, I just found out that...",
    "In the next 30 seconds, I'll teach you how to..."
];

const voiceoverOptions = [
    "educational tone",
    "casual tone",
    "sarcastic tone"
];

const videoOptions = [
    "fast cut",
    "standard cut",
    "slowmotion"
];

function MerraOrManual() {
    const { darkMode } = useDarkMode();
    const inputOutputContainerRef = useRef(null);
    const step1Ref = useRef(null);
    const step2Ref = useRef(null);
    const step3Ref = useRef(null);
    const processRef = useRef(null);
    const outputRef = useRef(null);
    const [isMobile, setIsMobile] = useState(false);
    const [isStackedLayout, setIsStackedLayout] = useState(false);
    const [scriptOptionIndex, setScriptOptionIndex] = useState(0);
    const [voiceoverOptionIndex, setVoiceoverOptionIndex] = useState(0);
    const [videoOptionIndex, setVideoOptionIndex] = useState(0);
    const [activeButtonIndex, setActiveButtonIndex] = useState(null);
    const [buttonOpacity, setButtonOpacity] = useState({ 0: 1, 1: 1, 2: 1 });
    const [shimmerButtonIndex, setShimmerButtonIndex] = useState(null);
    const [mousePointerPosition, setMousePointerPosition] = useState({ x: 0, y: 0, visible: true });
    const currentButtonIndexRef = useRef(0);
    const button0Ref = useRef(null);
    const button1Ref = useRef(null);
    const button2Ref = useRef(null);

    useEffect(() => {
        trackEvent('page_viewed', { pageName: 'MerraOrManual' });
    }, []);

    useEffect(() => {
        const checkScreenSize = () => {
            const width = window.innerWidth;
            setIsMobile(width <= 768);
            setIsStackedLayout(width <= 1020);
        };
        checkScreenSize();
        window.addEventListener('resize', checkScreenSize);
        return () => window.removeEventListener('resize', checkScreenSize);
    }, []);

    // Process section button cycling animation
    useEffect(() => {
        const getButtonPosition = (buttonRef) => {
            if (!buttonRef.current) return null;
            const rect = buttonRef.current.getBoundingClientRect();
            const containerRect = processRef.current?.getBoundingClientRect();
            if (!containerRect) return null;
            return {
                x: rect.left + rect.width / 2 - containerRect.left + 20, // 20px right
                y: rect.top + rect.height / 2 - containerRect.top + 20 // 20px lower
            };
        };

        const moveMouseToButton = (buttonIndex, callback) => {
            const buttonRefs = [button0Ref, button1Ref, button2Ref];
            const targetButton = buttonRefs[buttonIndex];
            
            // Small delay to ensure refs are mounted
            setTimeout(() => {
                const position = getButtonPosition(targetButton);
                
                if (position) {
                    // Get current position or start from first button if not visible
                    const currentPos = mousePointerPosition.visible 
                        ? { x: mousePointerPosition.x, y: mousePointerPosition.y }
                        : getButtonPosition(buttonRefs[0]) || { x: 0, y: 0 };
                    
                    // First, set mouse to current position (if not already visible)
                    if (!mousePointerPosition.visible) {
                        setMousePointerPosition({ ...currentPos, visible: true, scale: 1 });
                        // Small delay to ensure initial position is set
                        setTimeout(() => {
                            // Then animate to target button
                            setMousePointerPosition({ ...position, visible: true, scale: 1 });
                            
                            // After movement completes, do click animation
                            setTimeout(() => {
                                setMousePointerPosition(prev => ({ ...prev, scale: 0.8 }));
                                setTimeout(() => {
                                    setMousePointerPosition(prev => ({ ...prev, scale: 1 }));
                                    if (callback) callback();
                                }, 100);
                            }, 300); // Wait for movement transition (0.3s)
                        }, 50);
                    } else {
                        // Mouse is already visible, just move it to new position
                        setMousePointerPosition({ ...position, visible: true, scale: 1 });
                        
                        // After movement completes, do click animation
                        setTimeout(() => {
                            setMousePointerPosition(prev => ({ ...prev, scale: 0.8 }));
                            setTimeout(() => {
                                setMousePointerPosition(prev => ({ ...prev, scale: 1 }));
                                if (callback) callback();
                            }, 100);
                        }, 300); // Wait for movement transition (0.3s)
                    }
                } else {
                    // Fallback: try again or proceed without mouse
                    if (callback) callback();
                }
            }, 50);
        };

        const cycleButtons = () => {
            const currentButtonIndex = currentButtonIndexRef.current;
            
            // Reset opacity for all buttons
            setButtonOpacity({ 0: 1, 1: 1, 2: 1 });
            setShimmerButtonIndex(null);
            
            // Move mouse to current button
            moveMouseToButton(currentButtonIndex, () => {
                // Start shimmer effect after mouse arrives
                setShimmerButtonIndex(currentButtonIndex);
                
                // Animate button click (reduce opacity) - happens after shimmer completes (600ms) + 200ms delay
                setTimeout(() => {
                    setShimmerButtonIndex(null);
                    setActiveButtonIndex(currentButtonIndex);
                    
                    setButtonOpacity(prev => ({
                        ...prev,
                        [currentButtonIndex]: 0.5
                    }));
                    
                    // Reset opacity (button release) after 300ms
                    setTimeout(() => {
                        setButtonOpacity(prev => ({
                            ...prev,
                            [currentButtonIndex]: 1
                        }));
                        setActiveButtonIndex(null);
                    }, 200);
                }, 800); // 600ms shimmer duration + 200ms delay
            });
            
            // Update the corresponding option index after 0.5 seconds (after button is released)
            // Mouse move (300ms) + shimmer (600ms) + delay (200ms) + click animation (300ms) + 500ms after release = 1900ms
            setTimeout(() => {
                if (currentButtonIndex === 0) {
                    setScriptOptionIndex(prev => (prev + 1) % scriptOptions.length);
                } else if (currentButtonIndex === 1) {
                    setVoiceoverOptionIndex(prev => (prev + 1) % voiceoverOptions.length);
                } else if (currentButtonIndex === 2) {
                    setVideoOptionIndex(prev => (prev + 1) % videoOptions.length);
                }
                // Keep mouse visible for next cycle (don't hide it)
            }, 1900); // 300ms mouse move + 600ms shimmer + 200ms delay + 300ms click + 500ms after release
            
            // Move to next button
            currentButtonIndexRef.current = (currentButtonIndex + 1) % 3;
        };
        
        // Start cycling immediately, then every 2 seconds
        cycleButtons();
        const interval = setInterval(cycleButtons, 2000);
        
        return () => clearInterval(interval);
    }, []);


    return (
        <>
        <PageSeo
            title="Merra or Manual? – AI vs Manual Video Editing"
            description="What does Merra or Manual mean? Learn how Merra AI automated video creation compares to manual editing for social media creators."
            path="/merra-or-manual"
            keywords="Merra or manual, AI video vs manual editing, automated video creation, Merra explanation"
        />
        <div style={{ 
            maxWidth: '900px', 
            margin: '0 auto', 
            padding: '40px 20px',
            fontFamily: 'Arial, sans-serif',
            lineHeight: '1.8',
            color: darkMode ? '#e0e0e0' : '#333',
            backgroundColor: darkMode ? '#1a1a1a' : 'white',
            minHeight: '100vh'
        }}>
            <div style={{ marginBottom: '30px' }}>
                <Link to="/" style={{ color: darkMode ? '#4A9EFF' : '#1976d2', textDecoration: 'none' }}>
                    ← Back to Home
                </Link>
            </div>

            <article>
                <header style={{ marginBottom: '40px' }}>
                    <h1 style={{ 
                        color: darkMode ? '#ffffff' : '#2c3e50', 
                        fontSize: '42px',
                        fontWeight: 'bold',
                        lineHeight: '1.2',
                        marginBottom: '20px'
                    }}>
                        Merra or Manual? What does that mean? The Question No One Wants to Explain
                    </h1>
                    <p style={{ 
                        fontSize: '18px', 
                        color: darkMode ? '#b0b0b0' : '#666',
                        fontStyle: 'italic'
                    }}>
                        It's everywhere, TikTok, Instagram, YouTube Shorts, X...
                    </p>
                </header>

                <div style={{ fontSize: '18px', lineHeight: '1.9' }}>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        You have seen it in the comments of a ton of videos.
                    </p>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        It feels like an inside joke.
                    </p>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        There is a new question spreading through creators and founders.
                    </p>
                    <p style={{ 
                        marginBottom: '40px', 
                        fontSize: '24px',
                        fontWeight: 'bold',
                        color: darkMode ? '#ffffff' : '#2c3e50'
                    }}>
                        Merra or Manual?
                    </p>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        People ask it when they see a clean voiceover video.
                    </p>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        They ask it when they see a creator posting daily.
                    </p>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        They ask it when someone delivers a perfect hook and script, with smooth pacing and tight cuts.
                    </p>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        No one has written a real explanation.
                    </p>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        People reply with emojis.
                    </p>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        Some say "obviously Merra."
                    </p>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        Others say "manual, bro, spent hours on this."
                    </p>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        And everyone keeps arguing without actually saying what this question means.
                    </p>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        Some people think we started the trend ourselves as a marketing stunt.
                    </p>
                    <p style={{ marginBottom: '40px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        No confirmation. No denial.
                    </p>

                    <h2 style={{ 
                        fontSize: '32px',
                        fontWeight: 'bold',
                        color: darkMode ? '#ffffff' : '#2c3e50',
                        marginTop: '50px',
                        marginBottom: '30px',
                        borderTop: `2px solid ${darkMode ? '#AB5898' : '#AB5898'}`,
                        paddingTop: '30px'
                    }}>
                        Here's the meaning behind it.
                    </h2>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        There are only two ways to make content today.
                    </p>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        You do it manually.
                    </p>
                    <p style={{ marginBottom: '40px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        Or you let Merra do it for you.
                    </p>

                    <h2 style={{ 
                        fontSize: '32px',
                        fontWeight: 'bold',
                        color: darkMode ? '#ffffff' : '#2c3e50',
                        marginTop: '50px',
                        marginBottom: '30px',
                        borderTop: `2px solid ${darkMode ? '#AB5898' : '#AB5898'}`,
                        paddingTop: '30px'
                    }}>
                        Manual means:
                    </h2>
                    <ul style={{ 
                        marginBottom: '30px',
                        paddingLeft: '30px',
                        color: darkMode ? '#e0e0e0' : '#333'
                    }}>
                        <li style={{ marginBottom: '15px' }}>scripting</li>
                        <li style={{ marginBottom: '15px' }}>filming</li>
                        <li style={{ marginBottom: '15px' }}>trimming milliseconds</li>
                        <li style={{ marginBottom: '15px' }}>choosing clips</li>
                        <li style={{ marginBottom: '15px' }}>adding subtitles</li>
                        <li style={{ marginBottom: '15px' }}>cutting</li>
                        <li style={{ marginBottom: '15px' }}>recording the voice</li>
                        <li style={{ marginBottom: '15px' }}>exporting</li>
                        <li style={{ marginBottom: '15px' }}>fixing errors</li>
                        <li style={{ marginBottom: '15px' }}>re exporting</li>
                    </ul>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        For some, it lasts hours. Because for some, the content has to be perfect.
                    </p>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        For us, perfectionism means procrastination.
                    </p>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        For us, you have too much to say, so you should focus on the message instead of the cut that is on-beat.
                    </p>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        We started it because we are creators and we had a ton of content that we didn't post, a ton of ideas that we didn't edit, and we missed out on more visibility for a decade.
                    </p>
                    <p style={{ marginBottom: '40px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        That's what manual means.
                    </p>

                    <h2 style={{ 
                        fontSize: '32px',
                        fontWeight: 'bold',
                        color: darkMode ? '#ffffff' : '#2c3e50',
                        marginTop: '50px',
                        marginBottom: '30px',
                        borderTop: `2px solid ${darkMode ? '#AB5898' : '#AB5898'}`,
                        paddingTop: '30px'
                    }}>
                        Merra means:
                    </h2>
                    <ul style={{ 
                        marginBottom: '30px',
                        paddingLeft: '30px',
                        color: darkMode ? '#e0e0e0' : '#333'
                    }}>
                        <li style={{ marginBottom: '15px' }}>drop five clips</li>
                        <li style={{ marginBottom: '15px' }}>drop your company description</li>
                        <li style={{ marginBottom: '15px' }}>get a finished story</li>
                        <li style={{ marginBottom: '15px' }}>get consistent output</li>
                        <li style={{ marginBottom: '15px' }}>post daily without effort</li>
                        <li style={{ marginBottom: '15px' }}>keep your momentum</li>
                        <li style={{ marginBottom: '15px' }}>stay visible</li>
                    </ul>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        It is not an editor.
                    </p>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        It is not a template tool.
                    </p>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        It is a storytelling engine.
                    </p>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        You upload a few moments from your day and the system turns them into voiceover content, natural and personal.
                    </p>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        The hook, the script, the pacing, the voice, the captions.
                    </p>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        All done for you.
                    </p>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        Merra removes friction.
                    </p>
                    <p style={{ marginBottom: '40px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        And when friction disappears, creativity finally becomes consistent.
                    </p>

                    <section>
                        <div>
                            <div 
                                ref={inputOutputContainerRef}
                                className="input-output-container"
                                style={{ position: 'relative' }}
                            >
                                {!isMobile && !isStackedLayout && (<>
                                {/* Animated Beams - one from each step to process */}
                                <AnimatedBeam
                                    containerRef={inputOutputContainerRef}
                                    fromRef={step1Ref}
                                    toRef={processRef}
                                    curvature={80}
                                    duration={4}
                                />
                                <AnimatedBeam
                                    containerRef={inputOutputContainerRef}
                                    fromRef={step2Ref}
                                    toRef={processRef}
                                    curvature={40}
                                    duration={4.5}
                                />
                                <AnimatedBeam
                                    containerRef={inputOutputContainerRef}
                                    fromRef={step3Ref}
                                    toRef={processRef}
                                    curvature={0}
                                    duration={5}
                                />
                                {/* Animated Beam - from process to output */}
                                <AnimatedBeam
                                    containerRef={inputOutputContainerRef}
                                    fromRef={processRef}
                                    toRef={outputRef}
                                    curvature={0}
                                    duration={4}
                                />
                                </>)}
                                
                                {/* Input Steps */}
                                <div style={{
                                    flex: '1',
                                    maxWidth: '300px',
                                    width: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '24px',
                                    position: 'relative',
                                    zIndex: 2
                                }}>
                                    <h3 style={{
                                        margin: 0,
                                        fontSize: '24px',
                                        fontWeight: 'bold',
                                        color: 'var(--text-primary)',
                                        // color: '#333',
                                        width: '100%',
                                        textAlign: isMobile ? 'center' : 'left',
                                        height: '32px',
                                        lineHeight: '32px'
                                    }}>
                                        Input:
                                    </h3>
                                    {/* Step 1: Clone your voice */}
                                    <div 
                                        ref={step1Ref}
                                        style={{
                                            backgroundColor: 'var(--input-bg)',
                                            border: '1px solid var(--border-primary)',
                                            borderRadius: '12px',
                                            padding: '20px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '12px',
                                            position: 'relative'
                                        }}
                                    >
                                        <h3 style={{
                                            margin: 0,
                                            fontSize: '18px',
                                            fontWeight: 'bold',
                                            color: 'var(--text-primary)'
                                        }}>
                                            1. Voice sample
                                        </h3>
                                        <audio
                                            controls
                                            style={{
                                                maxWidth: '100%',
                                                height: '40px',
                                                border: '2px solid rgba(0,0,0,0.1)',
                                                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                                                borderRadius: '8px',
                                                padding: '1px 1px',
                                            }}
                                        >
                                            <source src="https://proto-filesharing.s3.eu-north-1.amazonaws.com/default-voices-samples/voice_preview_bouki.mp3" type="audio/mpeg" />
                                            Your browser does not support the audio element.
                                        </audio>
                                    </div>

                                    {/* Step 2: Describe your company */}
                                    <div 
                                        ref={step2Ref}
                                        style={{
                                            backgroundColor: 'var(--input-bg)',
                                            border: '1px solid var(--border-primary)',
                                            borderRadius: '12px',
                                            padding: '20px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '12px',
                                            position: 'relative'
                                        }}
                                    >
                                        <h3 style={{
                                            margin: 0,
                                            fontSize: '18px',
                                            fontWeight: 'bold',
                                            color: 'var(--text-primary)'
                                        }}>
                                            2. Company description
                                        </h3>
                                        <div style={{
                                            backgroundColor: 'var(--background-tertiary)',
                                            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                                            border: '2px solid rgba(0,0,0,0.1)',
                                            borderRadius: '8px',
                                            padding: '16px',
                                            fontSize: '14px',
                                            color: 'var(--text-secondary)',
                                            lineHeight: '1.6',
                                        }}>
                                            "AI short-form content tool"
                                        </div>
                                    </div>

                                    {/* Step 3: Upload your raw content */}
                                    <div 
                                        ref={step3Ref}
                                        style={{
                                            backgroundColor: 'var(--input-bg)',
                                            border: '1px solid var(--border-primary)',
                                            borderRadius: '12px',
                                            padding: '20px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '12px',
                                            position: 'relative'
                                        }}
                                    >
                                        <h3 style={{
                                            margin: 0,
                                            fontSize: '18px',
                                            fontWeight: 'bold',
                                            color: 'var(--text-primary)'
                                        }}>
                                            3. Raw videos
                                        </h3>
                                        <div style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '8px',
                                            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                                            border: '2px solid rgba(0,0,0,0.1)',
                                            borderRadius: '8px',
                                            padding: '16px',
                                        }}>
                                            {['Moments', 'Vibing', 'People'].map((folderName, index) => (
                                                <div 
                                                    key={index}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '12px',
                                                        padding: '16px',
                                                        backgroundColor: 'var(--background-tertiary)',
                                                        border: '1px solid var(--border-primary)',
                                                        borderRadius: '8px'
                                                    }}
                                                >
                                                    <svg 
                                                        width="24" 
                                                        height="24" 
                                                        viewBox="0 0 24 24" 
                                                        fill="none" 
                                                        stroke="currentColor" 
                                                        strokeWidth="2" 
                                                        strokeLinecap="round" 
                                                        strokeLinejoin="round"
                                                        style={{ color: 'var(--text-secondary)', flexShrink: 0 }}
                                                    >
                                                        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
                                                    </svg>
                                                    <span style={{
                                                        fontSize: '14px',
                                                        color: 'var(--text-secondary)',
                                                        fontWeight: '500'
                                                    }}>
                                                        {folderName}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {/* Arrow pointing to process */}
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                    padding: '0 20px',
                                    opacity: isMobile || isStackedLayout ? 1 : 0
                                }}>
                                    <svg 
                                        width="40" 
                                        height="40" 
                                        viewBox="0 0 24 24" 
                                        fill="none" 
                                        stroke="currentColor" 
                                        strokeWidth="2" 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round"
                                        style={{ color: 'var(--text-primary)' }}
                                    >
                                        <path d="M5 12h14M12 5l7 7-7 7"/>
                                    </svg>
                                </div>

                                {/* Process Section */}
                                <div 
                                    ref={processRef}
                                    style={{
                                        flex: '1',
                                        maxWidth: '300px',
                                        width: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '16px',
                                        position: 'relative',
                                        zIndex: 2,
                                        justifyContent: 'center',
                                        alignSelf: 'center'
                                    }}>
                                    {/* Animated Mouse Pointer */}
                                    {mousePointerPosition.visible && (
                                        <div
                                            style={{
                                                position: 'absolute',
                                                left: `${mousePointerPosition.x}px`,
                                                top: `${mousePointerPosition.y}px`,
                                                transform: `translate(-50%, -50%) scale(${mousePointerPosition.scale || 1})`,
                                                transition: mousePointerPosition.scale ? 'transform 0.1s ease' : 'left 0.3s ease, top 0.3s ease',
                                                pointerEvents: 'none',
                                                zIndex: 1000
                                            }}
                                        >
                                            <svg
                                                width="24"
                                                height="24"
                                                viewBox="0 0 24 24"
                                                fill="red"
                                                stroke="red"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                style={{ filter: 'drop-shadow(0 2px 4px rgba(255,0,0,0.5))' }}
                                            >
                                                <path d="M3 3l7.07 16.97 2.51-7.39 7.39-2.51L3 3z" />
                                            </svg>
                                        </div>
                                    )}
                                    <h3 style={{
                                        margin: 0,
                                        fontSize: '24px',
                                        fontWeight: 'bold',
                                        color: 'var(--text-primary)',
                                        // color: '#333',
                                        width: '100%',
                                        textAlign: isMobile ? 'center' : 'left'
                                    }}>
                                        Process:
                                    </h3>
                                    <div style={{
                                        backgroundColor: '#f5f5f5',
                                        borderRadius: '12px',
                                        padding: '20px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '16px',
                                        marginTop: 0
                                    }}>
                                        {/* Script */}
                                        <div style={{
                                            backgroundColor: 'var(--input-bg)',
                                            border: '1px solid var(--border-primary)',
                                            borderRadius: '8px',
                                            padding: '16px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '8px'
                                        }}>
                                            <h4 style={{
                                                margin: 0,
                                                fontSize: '16px',
                                                fontWeight: 'bold',
                                                color: 'var(--text-primary)'
                                            }}>
                                                Script
                                            </h4>
                                            <p style={{
                                                margin: 0,
                                                fontSize: '14px',
                                                color: 'var(--text-secondary)',
                                                fontStyle: 'italic'
                                            }}>
                                                "{scriptOptions[scriptOptionIndex]}"
                                            </p>
                                            <button
                                                ref={button0Ref}
                                                style={{
                                                    padding: '8px 16px',
                                                    fontSize: '14px',
                                                    backgroundColor: '#e1f679',
                                                    border: '1px solid var(--border-primary)',
                                                    borderRadius: '6px',
                                                    color: 'var(--text-primary)',
                                                    cursor: 'default',
                                                    opacity: buttonOpacity[0],
                                                    transition: 'opacity 0.3s ease',
                                                    pointerEvents: 'none',
                                                    alignSelf: 'center',
                                                    marginTop: '4px'
                                                }}
                                            >
                                                <span className={shimmerButtonIndex === 0 ? 're-generate-shimmer' : ''} style={{ color: 'black' }}>
                                                    re-generate
                                                </span>
                                            </button>
                                        </div>

                                        {/* Voiceover */}
                                        <div style={{
                                            backgroundColor: 'var(--input-bg)',
                                            border: '1px solid var(--border-primary)',
                                            borderRadius: '8px',
                                            padding: '16px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '8px'
                                        }}>
                                            <h4 style={{
                                                margin: 0,
                                                fontSize: '16px',
                                                fontWeight: 'bold',
                                                color: 'var(--text-primary)'
                                            }}>
                                                Voiceover
                                            </h4>
                                            <p style={{
                                                margin: 0,
                                                fontSize: '14px',
                                                color: 'var(--text-secondary)',
                                                fontStyle: 'italic'
                                            }}>
                                                "{voiceoverOptions[voiceoverOptionIndex]}"
                                            </p>
                                            <button
                                                ref={button1Ref}
                                                style={{
                                                    padding: '8px 16px',
                                                    fontSize: '14px',
                                                    backgroundColor: '#e1f679',
                                                    border: '1px solid var(--border-primary)',
                                                    borderRadius: '6px',
                                                    color: 'var(--text-primary)',
                                                    cursor: 'default',
                                                    opacity: buttonOpacity[1],
                                                    transition: 'opacity 0.3s ease',
                                                    pointerEvents: 'none',
                                                    alignSelf: 'center',
                                                    marginTop: '4px'
                                                }}
                                            >
                                                <span className={shimmerButtonIndex === 1 ? 're-generate-shimmer' : ''} style={{ color: 'black' }}>
                                                    re-generate
                                                </span>
                                            </button>
                                        </div>

                                        {/* Video */}
                                        <div style={{
                                            backgroundColor: 'var(--input-bg)',
                                            border: '1px solid var(--border-primary)',
                                            borderRadius: '8px',
                                            padding: '16px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '8px'
                                        }}>
                                            <h4 style={{
                                                margin: 0,
                                                fontSize: '16px',
                                                fontWeight: 'bold',
                                                color: 'var(--text-primary)'
                                            }}>
                                                Video
                                            </h4>
                                            <p style={{
                                                margin: 0,
                                                fontSize: '14px',
                                                color: 'var(--text-secondary)',
                                                fontStyle: 'italic'
                                            }}>
                                                "{videoOptions[videoOptionIndex]}"
                                            </p>
                                            <button
                                                ref={button2Ref}
                                                style={{
                                                    padding: '8px 16px',
                                                    fontSize: '14px',
                                                    backgroundColor: '#e1f679',
                                                    border: '1px solid var(--border-primary)',
                                                    borderRadius: '6px',
                                                    color: 'var(--text-primary)',
                                                    cursor: 'default',
                                                    opacity: buttonOpacity[2],
                                                    transition: 'opacity 0.3s ease',
                                                    pointerEvents: 'none',
                                                    alignSelf: 'center',
                                                    marginTop: '4px'
                                                }}
                                            >
                                                <span className={shimmerButtonIndex === 2 ? 're-generate-shimmer' : ''} style={{ color: 'black' }}>
                                                    re-generate
                                                </span>
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Arrow pointing to output */}
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                    padding: '0 20px',
                                    opacity: isMobile || isStackedLayout ? 1 : 0
                                }}>
                                    <svg 
                                        width="40" 
                                        height="40" 
                                        viewBox="0 0 24 24" 
                                        fill="none" 
                                        stroke="currentColor" 
                                        strokeWidth="2" 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round"
                                        style={{ color: 'var(--text-primary)' }}
                                    >
                                        <path d="M5 12h14M12 5l7 7-7 7"/>
                                    </svg>
                                </div>

                                {/* Output Video */}
                                <div 
                                    ref={outputRef}
                                    style={{
                                        flex: '1',
                                        maxWidth: '300px',
                                        width: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '16px',
                                        position: 'relative',
                                        zIndex: 2,
                                    }}
                                >
                                    <h3 style={{
                                        margin: 0,
                                        fontSize: '24px',
                                        fontWeight: 'bold',
                                        color: 'var(--text-primary)',
                                        // color: '#333',
                                        width: '100%',
                                        textAlign: isMobile ? 'center' : 'left'
                                    }}>
                                        Output:
                                    </h3>
                                    <div className="video-container">
                                        <video
                                            src="https://proto-filesharing.s3.eu-north-1.amazonaws.com/public/Example-New-GenerateVideoWithVoiceover-Merra-2.mp4"
                                            controls
                                            style={{
                                                maxWidth: '600px',
                                                maxHeight: '600px',
                                                minHeight: '600px',
                                                borderRadius: '12px',
                                                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                                            }}
                                            playsInline
                                        >
                                            <source src="https://proto-filesharing.s3.eu-north-1.amazonaws.com/public/Example-New-GenerateVideoWithVoiceover-Merra-2.mp4" type="video/mp4" />
                                            Your browser does not support the video tag.
                                        </video>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    <h2 style={{ 
                        fontSize: '32px',
                        fontWeight: 'bold',
                        color: darkMode ? '#ffffff' : '#2c3e50',
                        marginTop: '50px',
                        marginBottom: '30px',
                        borderTop: `2px solid ${darkMode ? '#AB5898' : '#AB5898'}`,
                        paddingTop: '30px'
                    }}>
                        The Philosophy: The Courage of Imperfection
                    </h2>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        Merra celebrates the courage of the imperfect.
                    </p>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        Because the AI won't always make something perfect.
                    </p>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        And sometimes, you wish you had control over that.
                    </p>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        You get a result that is 97% there.
                    </p>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        Now you wish you could move your clips on a timeline, but the software won't let you.
                    </p>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        The only options are: it either generates something else, with the risk to get further from what you wanted, or you take that imperfect video and you find the courage to post anyway.
                    </p>
                    <p style={{ marginBottom: '40px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        Because as soon as you start moving clips on a timeline, we know you will spend 3 hours doing it, for a difference that won't matter that much.
                    </p>
                    <p style={{ marginBottom: '40px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        Tough reality for creators, but you know it's true: perfectionism is the enemy of consistency.
                    </p>

                    <h2 style={{ 
                        fontSize: '32px',
                        fontWeight: 'bold',
                        color: darkMode ? '#ffffff' : '#2c3e50',
                        marginTop: '50px',
                        marginBottom: '30px',
                        borderTop: `2px solid ${darkMode ? '#AB5898' : '#AB5898'}`,
                        paddingTop: '30px'
                    }}>
                        Why creators ask the question
                    </h2>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        You see the question "Merra or Manual?" even when it's obvious that it's not made by Merra.
                    </p>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        It is not only about tools.
                    </p>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        It is a signal.
                    </p>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        It means:
                    </p>
                    <p style={{ 
                        marginBottom: '40px',
                        fontSize: '20px',
                        fontStyle: 'italic',
                        color: darkMode ? '#ffffff' : '#2c3e50',
                        paddingLeft: '20px',
                        borderLeft: `4px solid ${darkMode ? '#AB5898' : '#AB5898'}`
                    }}>
                        I see you, and I'm a creator too, I'm an insider.
                    </p>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        And when the recipient doesn't know the answer, it becomes an invitation, to a new conversation, to a new way.
                    </p>

                    <h2 style={{ 
                        fontSize: '32px',
                        fontWeight: 'bold',
                        color: darkMode ? '#ffffff' : '#2c3e50',
                        marginTop: '50px',
                        marginBottom: '30px',
                        borderTop: `2px solid ${darkMode ? '#AB5898' : '#AB5898'}`,
                        paddingTop: '30px'
                    }}>
                        The rise of Merra content
                    </h2>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        When you scroll on TikTok or Instagram, you start to notice patterns:
                    </p>
                    <ul style={{ 
                        marginBottom: '30px',
                        paddingLeft: '30px',
                        color: darkMode ? '#e0e0e0' : '#333'
                    }}>
                        <li style={{ marginBottom: '15px' }}>smooth voiceovers</li>
                        <li style={{ marginBottom: '15px' }}>short B roll clips</li>
                        <li style={{ marginBottom: '15px' }}>fast pacing</li>
                        <li style={{ marginBottom: '15px' }}>strong hooks</li>
                        <li style={{ marginBottom: '15px' }}>a story that actually says something</li>
                    </ul>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        And every time you notice one, you wonder.
                    </p>
                    <p style={{ 
                        marginBottom: '25px',
                        fontSize: '24px',
                        fontWeight: 'bold',
                        color: darkMode ? '#ffffff' : '#2c3e50'
                    }}>
                        Merra or Manual?
                    </p>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        This question becomes the new watermark.
                    </p>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        It becomes part of the culture.
                    </p>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        A signature.
                    </p>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        Some say it is becoming the new "Shot on iPhone."
                    </p>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        A wink between people who understand how creators stay consistent.
                    </p>
                    <p style={{ marginBottom: '40px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        This is why we built Merra.
                    </p>
                    <p style={{ marginBottom: '40px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        To make daily storytelling something anyone can do without stress or burnout.
                    </p>

                    <h2 style={{ 
                        fontSize: '32px',
                        fontWeight: 'bold',
                        color: darkMode ? '#ffffff' : '#2c3e50',
                        marginTop: '50px',
                        marginBottom: '30px',
                        borderTop: `2px solid ${darkMode ? '#AB5898' : '#AB5898'}`,
                        paddingTop: '30px'
                    }}>
                        Why the question matters
                    </h2>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        "Merra or Manual?" is not just a meme. It's not a trend. It's an invitation to join a movement, a new world.
                    </p>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        Merra is the new world.
                    </p>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        Fast.
                    </p>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        Simple.
                    </p>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        Natural.
                    </p>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        Consistent.
                    </p>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        It's a matter of mindset, the Merra movement is about "keep posting", even when you don't feel like it, because when creating is as easy as writing a tweet, you have no excuse.
                    </p>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        Now it doesn't mean you shouldn't edit content manually, it means: while you spend 4 days editing the masterpiece, you can also post some Merra-edited content the other 3 days.
                    </p>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        The question matters because it invites you to rethink your workflow.
                    </p>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        To ask yourself where your time and energy should go.
                    </p>
                    <p style={{ marginBottom: '40px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        To consider a new way of being present online.
                    </p>

                    <h2 style={{ 
                        fontSize: '32px',
                        fontWeight: 'bold',
                        color: darkMode ? '#ffffff' : '#2c3e50',
                        marginTop: '50px',
                        marginBottom: '30px',
                        borderTop: `2px solid ${darkMode ? '#AB5898' : '#AB5898'}`,
                        paddingTop: '30px'
                    }}>
                        Try the experiment
                    </h2>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        This week, try this:
                    </p>
                    <ul style={{ 
                        marginBottom: '30px',
                        paddingLeft: '30px',
                        color: darkMode ? '#e0e0e0' : '#333'
                    }}>
                        <li style={{ marginBottom: '15px' }}>Make one video manually.</li>
                        <li style={{ marginBottom: '15px' }}>Make one video with Merra.</li>
                    </ul>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        Then ask yourself honestly:
                    </p>
                    <ul style={{ 
                        marginBottom: '30px',
                        paddingLeft: '30px',
                        color: darkMode ? '#e0e0e0' : '#333'
                    }}>
                        <li style={{ marginBottom: '15px' }}>Which one felt lighter?</li>
                        <li style={{ marginBottom: '15px' }}>Which one made you want to post more?</li>
                        <li style={{ marginBottom: '15px' }}>Which one made you feel like you can actually keep going?</li>
                    </ul>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        This is where the real shift happens.
                    </p>
                    <p style={{ marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' }}>
                        Because once you feel the difference, you understand the question.
                    </p>
                    <p style={{ 
                        marginBottom: '40px',
                        marginTop: '40px',
                        fontSize: '28px',
                        fontWeight: 'bold',
                        color: darkMode ? '#ffffff' : '#2c3e50',
                        textAlign: 'center'
                    }}>
                        Merra or Manual?
                    </p>
                </div>
            </article>
            <Footer />
        </div>
        </>
    );
}

export default MerraOrManual;

